import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { DUR, EASE_OUT } from '../utils/motion';

ProgressRing.propTypes = {
  value: PropTypes.number,
  goal: PropTypes.number,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
  color: PropTypes.string,
  trackColor: PropTypes.string,
  reducedMotion: PropTypes.bool,
  children: PropTypes.node,
};

/**
 * The one progress ring. Extracted from DailyQuestCard so the navbar pill and
 * the streak sheet render the same geometry at different sizes rather than two
 * hand-rolled arcs that drift apart.
 *
 * Rotated -90deg so the arc starts at 12 o'clock; `strokeDashoffset` carries
 * the fill so the transition animates the arc itself.
 */
export default function ProgressRing({
  value = 0,
  goal = 3,
  size = 24,
  strokeWidth = 2.5,
  color,
  trackColor,
  reducedMotion = false,
  children,
}) {
  const safeGoal = goal > 0 ? goal : 1;
  const ratio = Math.max(0, Math.min(1, value / safeGoal));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - ratio);

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <Box
        component="svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden
        sx={{ display: 'block', transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          stroke={trackColor || 'rgba(0,0,0,0.12)'}
        />
        <Box
          component="circle"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          stroke={color}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          sx={{
            transition: reducedMotion ? 'none' : `stroke-dashoffset ${DUR.bar}ms ${EASE_OUT}`,
          }}
        />
      </Box>
      {children ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </Box>
      ) : null}
    </Box>
  );
}
